import React from "react";
import { BiLogoDiscordAlt, BiSolidRocket } from "react-icons/bi";
import { BsEnvelopeFill, BsGithub } from "react-icons/bs";
import { Link } from "react-router-dom";

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const handleSmoothScroll = (e, href) => {
    // Only handle internal hash links
    if (href && href.startsWith('#')) {
      e.preventDefault();
      const targetId = href.substring(1); // Remove '#'
      const targetElement = document.getElementById(targetId);
      
      if (targetElement) {
        targetElement.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    }
  };

  const footerLinks = {
    main: [
      { name: "الرئيسية", path: "/" },
      { name: "التسجيل", path: "/apply" },
    ],
    sections: [
      { name: "عن الهاكاثون", href: "#about" },
      { name: "الجدول الزمني", href: "#timeline" },
      { name: "الجائزة", href: "#prize" },
      { name: "الأسئلة الشائعة", href: "#faq" },
    ],
  };

  return (
    <footer className="relative bg-gradient-to-b from-blue to-dark-blue">
      {/* Wave Divider */}
      <div className="absolute -top-[calc(3rem_-_1px)] left-0 w-full overflow-hidden leading-none rotate-180">
        <svg
          className="relative block w-full h-12"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 1200 120"
          preserveAspectRatio="none"
        >
          <path
            d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z"
            className="fill-blue"
          ></path>
        </svg>
      </div>

      <div className="container mx-auto px-6 md:px-12 pt-20 pb-8 relative z-10">
        <div className="max-w-6xl mx-auto">
          {/* Main Footer Content */}
          <div className="grid md:grid-cols-3 gap-12 mb-12">
            {/* Logo and Description */}
            <div className="text-start">
              <div className="flex items-center justify-start gap-3 mb-4">
                <h3 className="text-3xl font-semibold text-white">
                  قمة هاكاثون
                </h3>
                <img
                  src={`${process.env.PUBLIC_URL}/logo.svg`}
                  alt="Qimma Logo"
                  className="w-10 h-10"
                />
              </div>
              <p className="text-white/70 leading-relaxed mb-4">
                هاكاثون عربي للمطورين العرب. نكتب الكود بالعربية، ونبني المستقبل
                للمبرمج العربي.
              </p>
              <div className="flex items-center justify-start gap-2 text-yellow">
                <span className="text-2xl">
                  <BiSolidRocket />
                </span>
                <span className="font-semibold">
                  ابنِ أداة تمكّن المبرمج العربي
                </span>
              </div>
            </div>

            {/* Quick Links */}
            <div className="text-center md:text-right">
              <h4 className="text-xl font-semibold text-white mb-4">
                روابط سريعة
              </h4>
              <ul className="space-y-2">
                {footerLinks.main.map((link, index) => (
                  <li key={index}>
                    <Link
                      to={link.path}
                      className="text-white/70 hover:text-yellow transition-colors  inline-block"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Sections */}
            <div className="text-center md:text-right">
              <h4 className="text-xl font-semibold text-white mb-4">الأقسام</h4>
              <ul className="space-y-2">
                {footerLinks.sections.map((link, index) => (
                  <li key={index}>
                    <a
                      href={link.href}
                      onClick={(e) => handleSmoothScroll(e, link.href)}
                      className="text-white/70 hover:text-yellow transition-colors  inline-block"
                    >
                      {link.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Social Links */}
          <div className="border-t border-white/10 pt-8 mb-8">
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="https://discord.gg/jpRGNsyfcw"
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center transition-all  hover:scale-110 border border-white/10"
                title="Discord"
              >
                <span className="text-3xl text-yellow">
                  <BiLogoDiscordAlt />
                </span>
              </a>
              <a
                href="https://github.com/Q1mma"
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center transition-all  hover:scale-110 border border-white/10"
                title="GitHub"
              >
                <span className="text-3xl text-yellow">
                  <BsGithub />
                </span>
              </a>
              <a
                href="mailto:contact@qimma.dev"
                className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center transition-all  hover:scale-110 border border-white/10"
                title="Email"
              >
                <span className="text-3xl text-yellow">
                  <BsEnvelopeFill />
                </span>
              </a>
            </div>
          </div>

          {/* Copyright */}
          <div className="border-t border-white/10 pt-8 text-center">
            <p className="text-white/60 mb-2">
              © {currentYear} قمة هاكاثون — هاكاثون عربي للمطورين العرب
            </p>
            <p className="text-white/40 text-sm">
              نكتب الكود بالعربية، ونبني المستقبل للمبرمج العربي 🌟
            </p>
          </div>

          {/* Decorative Elements */}
          <div className="mt-8 flex justify-center gap-2">
            <div className="w-2 h-2 rounded-full bg-yellow animate-pulse"></div>
            <div className="w-2 h-2 rounded-full bg-[#41a67e] animate-pulse delay-200"></div>
            <div className="w-2 h-2 rounded-full bg-[#7b3ff3] animate-pulse delay-300"></div>
            <div className="w-2 h-2 rounded-full bg-[#ff3ca6] animate-pulse delay-500"></div>
          </div>
        </div>
      </div>

      {/* Background Glow */}
      <div className="absolute bottom-0 left-1/4 w-64 h-64 bg-yellow rounded-full mix-blend-multiply filter blur-3xl opacity-5"></div>
      <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-[#7b3ff3] rounded-full mix-blend-multiply filter blur-3xl opacity-5"></div>
    </footer>
  );
};

export default Footer;
